import { useState } from 'react'
import {
  type LocationFilterBanner,
  parseLocCodes,
  useAdminLocationsConfig,
  useUpdateLocationsConfig,
} from '../../../features/admin/use-admin-locations'
import { AdminPage, adminError } from './admin-bits'
import { Button, Field, Inline, inputCls } from './controls'
import { Card, mono } from './ui'

function LocField({
  banner,
  field,
  label,
  hint,
  saved,
}: {
  banner: LocationFilterBanner
  field: 'excludedLocs' | 'allowedLocs'
  label: string
  hint: string
  saved: string[]
}) {
  const update = useUpdateLocationsConfig()
  const [text, setText] = useState(saved.join(', '))
  const parsed = parseLocCodes(text)
  const dirty = parsed.join(',') !== saved.join(',')
  return (
    <div>
      <Field label={label} hint={hint}>
        <textarea
          className={`${inputCls} h-20 py-2`}
          style={mono}
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
      </Field>
      <div className="mt-2 flex items-center justify-between gap-3">
        <span className="text-[12px] text-[var(--color-muted)]">{parsed.length} codes</span>
        <div className="flex items-center gap-3">
          {update.error ? <Inline tone="bad">{adminError(update.error)}</Inline> : null}
          {update.isSuccess && !dirty ? <Inline tone="ok">Saved</Inline> : null}
          <Button disabled={!dirty || update.isPending} onClick={() => update.mutate({ banner, [field]: parsed })}>
            {update.isPending ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  )
}

export function PorcelainAdminLocations() {
  const cfg = useAdminLocationsConfig()
  const banners: [LocationFilterBanner, string][] = [
    ['dtlr', 'DTLR'],
    ['spc', 'SPC'],
  ]
  return (
    <AdminPage
      title="Location filters"
      subtitle="Hide or restrict store codes per banner. Codes are separated by commas, spaces or new lines."
      loading={cfg.isLoading}
      error={!!cfg.error}
    >
      {banners.map(([b, label]) => (
        <Card key={b} title={label}>
          <div className="grid gap-6 p-5 md:grid-cols-2">
            <LocField
              banner={b}
              field="excludedLocs"
              label="Excluded locations"
              hint="Never shown for this banner."
              saved={cfg.data?.[b].excludedLocs ?? []}
            />
            <LocField
              banner={b}
              field="allowedLocs"
              label="Allowed locations"
              hint="When set, only these are shown."
              saved={cfg.data?.[b].allowedLocs ?? []}
            />
          </div>
        </Card>
      ))}
    </AdminPage>
  )
}
